import React, { useState, useEffect } from 'react';
import { useSummer2026 } from '../context/Summer2026Context';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  ArrowLeft, Bus, RefreshCw, Check, X, Clock, AlertTriangle, Search, Printer, ChevronDown, ChevronUp
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const safeFetch = (...args) => (window.__origFetch || window.fetch)(...args);
const API = typeof window !== 'undefined' && window.location.hostname !== 'localhost' && !window.location.hostname.includes('preview.emergentagent.com') ? `${window.location.origin}/api` : `${process.env.REACT_APP_BACKEND_URL}/api`;
export default function BusAttendanceViewer({ date, onClose }) {
  const { busAttendance, syncBusAttendance, busLastSync, roster, fetchBusAttendance } = useSummer2026();
  const [syncing, setSyncing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedBus, setExpandedBus] = useState(null);
  const [localData, setLocalData] = useState(null);
  const [showBusDatePicker, setShowBusDatePicker] = useState(false);
  const [overrideDate, setOverrideDate] = useState(null);
  const [marking, setMarking] = useState({});

  // Use override date, provided date, or fallback to today EDT
  const todayEDT = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const todayStr = `${todayEDT.getFullYear()}-${String(todayEDT.getMonth()+1).padStart(2,'0')}-${String(todayEDT.getDate()).padStart(2,'0')}`;
  const viewDate = overrideDate || date || todayStr;

  const handleSync = async () => {
    setSyncing(true);
    try {
      // Trigger sync for the specific date
      const syncRes = await safeFetch(`${API}/summer2026/bus-attendance/sync?date=${viewDate}`);
      await syncRes.json();
      // Then fetch the stored data
      const res = await safeFetch(`${API}/summer2026/bus-attendance?date=${viewDate}`);
      const fetchedData = await res.json();
      if (fetchedData.success) {
        setLocalData(fetchedData);
        // Also refresh context so Group Login camper cards update their bus status pills
        fetchBusAttendance(viewDate);
      }
    } catch (err) {
      console.error('Bus sync error:', err);
    } finally {
      setSyncing(false);
    }
  };

  // Mark a camper's bus status locally (with backend persistence)
  const handleMarkStatus = async (record, newStatus) => {
    const markKey = `${record.first_name}|${record.last_name}`;
    setMarking(prev => ({ ...prev, [markKey]: true }));
    try {
      const res = await safeFetch(`${API}/summer2026/bus-attendance/mark`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: record.first_name,
          last_name: record.last_name,
          status: newStatus,
          bus_number: record.bus_number || '',
          date: viewDate,
          camper_id: record.camper_id || '',
        }),
      });
      const result = await res.json();
      if (result.success) {
        // Optimistically update local state
        setLocalData(prev => {
          if (!prev) return prev;
          const updatedRecords = prev.records.map(r => {
            if (r.first_name.toLowerCase() === record.first_name.toLowerCase() &&
                r.last_name.toLowerCase() === record.last_name.toLowerCase()) {
              return { ...r, status: newStatus, override: true, marked_at: new Date().toISOString() };
            }
            return r;
          });
          // Recalculate totals
          const totalPresent = updatedRecords.filter(r => r.status === 'present').length;
          const totalAbsent = updatedRecords.filter(r => r.status === 'absent').length;
          const totalUnmarked = updatedRecords.filter(r => r.status === 'unmarked' || r.status === 'late').length;
          // Update bus summaries
          const updatedBuses = (prev.buses || []).map(bus => {
            const busRecs = updatedRecords.filter(r => r.bus_number === bus.bus_number);
            return {
              ...bus,
              present: busRecs.filter(r => r.status === 'present').length,
              absent: busRecs.filter(r => r.status === 'absent').length,
              unmarked: busRecs.filter(r => r.status === 'unmarked' || r.status === 'late').length,
            };
          });
          return {
            ...prev,
            records: updatedRecords,
            buses: updatedBuses,
            totals: { ...prev.totals, total_present: totalPresent, total_absent: totalAbsent, total_unmarked: totalUnmarked },
          };
        });
        // Refresh context so Group Login camper cards update their bus status pills
        fetchBusAttendance(viewDate);
      }
    } catch (err) {
      console.error('Mark bus attendance error:', err);
    } finally {
      setMarking(prev => ({ ...prev, [markKey]: false }));
    }
  };

  // Auto-sync on mount if no data
  useEffect(() => {
    if (!localData || localData.date !== viewDate) {
      handleSync();
    }
  }, [viewDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const data = localData?.date === viewDate ? localData : null;
  const buses = data?.buses || [];
  const records = data?.records || [];
  const totals = data?.totals || {};
  const nameTobus = data?.name_to_bus || {};

  // Match records to CampMinder roster
  const matchedRecords = records.map(rec => {
    const nameKey = `${rec.first_name.trim().toLowerCase()}|${rec.last_name.trim().toLowerCase()}`;
    const camper = roster.find(c =>
      c.firstName.trim().toLowerCase() === rec.first_name.trim().toLowerCase() &&
      c.lastName.trim().toLowerCase() === rec.last_name.trim().toLowerCase()
    );
    return { ...rec, camper, nameKey };
  });

  const q = searchQuery.trim().toLowerCase();
  const filteredRecords = q
    ? matchedRecords.filter(r =>
        `${r.first_name} ${r.last_name}`.toLowerCase().includes(q) ||
        `${r.last_name} ${r.first_name}`.toLowerCase().includes(q) ||
        `${r.last_name}, ${r.first_name}`.toLowerCase().includes(q)
      )
    : matchedRecords;

  // Group records by bus
  const recordsByBus = {};
  filteredRecords.forEach(r => {
    if (!recordsByBus[r.bus_number]) recordsByBus[r.bus_number] = [];
    recordsByBus[r.bus_number].push(r);
  });

  const dateDisplay = viewDate
    ? new Date(viewDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
    : 'Today';

  const statusIcon = (status) => {
    switch (status) {
      case 'present': return <Check className="w-3.5 h-3.5 text-green-600" />;
      case 'absent': return <X className="w-3.5 h-3.5 text-red-600" />;
      case 'late': return <Clock className="w-3.5 h-3.5 text-amber-600" />;
      default: return <AlertTriangle className="w-3.5 h-3.5 text-gray-400" />;
    }
  };

  const statusBadge = (status) => {
    const styles = {
      present: 'bg-green-100 text-green-800 border-green-200',
      absent: 'bg-red-100 text-red-800 border-red-200',
      late: 'bg-amber-100 text-amber-800 border-amber-200',
      unmarked: 'bg-gray-100 text-gray-600 border-gray-200',
    };
    const labels = { present: 'On Bus', absent: 'Absent', late: 'Late (Bus)', unmarked: 'Unmarked' };
    return (
      <Badge className={`text-xs px-2 py-0.5 border ${styles[status] || styles.unmarked}`} data-testid={`bus-status-badge-${status}`}>
        {statusIcon(status)}
        <span className="ml-1">{labels[status] || status}</span>
      </Badge>
    );
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
      <head>
        <title>Bus Attendance - ${dateDisplay}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; color: #333; }
          h1 { font-size: 18px; text-align: center; margin-bottom: 4px; }
          h2 { font-size: 14px; text-align: center; color: #666; margin-bottom: 20px; font-weight: normal; }
          .bus-section { margin-bottom: 20px; page-break-inside: avoid; }
          .bus-header { background: #f1f5f9; padding: 8px 12px; border-radius: 6px; font-weight: bold; margin-bottom: 8px; }
          .record { display: flex; justify-content: space-between; padding: 4px 12px; border-bottom: 1px solid #eee; font-size: 13px; }
          .status-present { color: #15803d; }
          .status-absent { color: #dc2626; font-weight: bold; }
          .status-late { color: #d97706; font-weight: bold; }
          .status-unmarked { color: #9ca3af; }
          .summary { text-align: center; font-size: 13px; color: #666; margin-top: 20px; padding-top: 12px; border-top: 1px solid #ddd; }
          @media print { body { padding: 10px; } }
        </style>
      </head>
      <body>
        <h1>Rolling River Day Camp - Bus Attendance Report</h1>
        <h2>${dateDisplay}</h2>
        ${buses.length === 0 ? '<p style="text-align:center;color:#999;">No bus data synced for this date</p>' :
          buses.map(bus => {
            const busRecords = records.filter(r => r.bus_number === bus.bus_number);
            return `
              <div class="bus-section">
                <div class="bus-header">${bus.bus_number} — ${bus.present} present, ${bus.absent} absent${bus.unmarked > 0 ? `, ${bus.unmarked} unmarked` : ''}${bus.all_marked_late ? ' (unmarked marked LATE)' : ''}</div>
                ${busRecords.map(r => `
                  <div class="record">
                    <span>${r.last_name}, ${r.first_name}</span>
                    <span class="status-${r.status}">${r.status === 'present' ? 'On Bus' : r.status === 'absent' ? 'ABSENT' : r.status === 'late' ? 'LATE' : 'Unmarked'}</span>
                  </div>
                `).join('')}
              </div>
            `;
          }).join('')
        }
        <div class="summary">
          ${totals.buses_count || 0} buses | ${totals.total_present || 0} present | ${totals.total_absent || 0} absent | ${totals.total_unmarked || 0} unmarked
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  return (
    <div className="min-h-screen bg-[#F1F5F9] pb-24">
      <div className="bg-[#0055A4] py-4 px-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center">
            <button onClick={onClose} className="text-white mr-3" data-testid="bus-viewer-close">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-white text-xl font-bold font-['Fredoka']">Bus Attendance</h1>
              <p
                className="text-white/70 text-xs mt-0.5 cursor-pointer hover:text-white/90 transition-colors"
                onClick={() => setShowBusDatePicker(!showBusDatePicker)}
                data-testid="bus-date-toggle"
              >
                {dateDisplay}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={handlePrint}
              variant="ghost"
              className="text-white hover:bg-white/20 rounded-xl"
              data-testid="bus-print-btn"
            >
              <Printer className="w-5 h-5" />
            </Button>
            <Button
              onClick={handleSync}
              variant="ghost"
              className="text-white hover:bg-white/20 rounded-xl"
              disabled={syncing}
              data-testid="bus-sync-btn"
            >
              <RefreshCw className={`w-5 h-5 ${syncing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
        {showBusDatePicker && (
          <div className="max-w-3xl mx-auto flex justify-center mt-2 pb-1" data-testid="bus-date-picker">
            <input
              type="date"
              value={viewDate}
              onChange={e => {
                if (e.target.value) {
                  setOverrideDate(e.target.value);
                  setShowBusDatePicker(false);
                }
              }}
              className="border rounded-lg px-3 py-1.5 text-sm font-medium bg-white/90"
              data-testid="bus-date-input"
            />
          </div>
        )}
      </div>

      <div className="max-w-3xl mx-auto px-4 py-4">
        {/* Sync info */}
        {data?.synced_at && (
          <p className="text-xs text-[#475569] mb-3">
            Last synced: {new Date(data.synced_at).toLocaleString()}
          </p>
        )}

        {/* Summary cards */}
        {data && (
          <div className="grid grid-cols-4 gap-2 mb-4">
            <div className="bg-white rounded-xl border p-3 text-center">
              <p className="text-2xl font-bold text-[#0F172A]">{totals.buses_count || 0}</p>
              <p className="text-xs text-[#475569]">Buses</p>
            </div>
            <div className="bg-green-50 rounded-xl border border-green-200 p-3 text-center">
              <p className="text-2xl font-bold text-green-700">{totals.total_present || 0}</p>
              <p className="text-xs text-green-600">On Bus</p>
            </div>
            <div className="bg-red-50 rounded-xl border border-red-200 p-3 text-center">
              <p className="text-2xl font-bold text-red-700">{totals.total_absent || 0}</p>
              <p className="text-xs text-red-600">Absent</p>
            </div>
            <div className="bg-amber-50 rounded-xl border border-amber-200 p-3 text-center">
              <p className="text-2xl font-bold text-amber-700">{totals.total_unmarked || 0}</p>
              <p className="text-xs text-amber-600">Unmarked</p>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
          <Input
            data-testid="bus-search"
            placeholder="Search camper by name..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-9 h-10 rounded-xl border-[#E2E8F0] bg-white text-sm"
          />
          {q && (
            <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8]">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Bus list */}
        {!data && !syncing && (
          <div className="bg-white rounded-2xl border p-8 text-center">
            <Bus className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-[#475569] font-medium">No bus attendance data</p>
            <p className="text-xs text-[#94A3B8] mt-1">Tap the sync button to fetch bus attendance</p>
          </div>
        )}

        {syncing && (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 text-[#0055A4] animate-spin" />
            <span className="ml-3 text-[#475569]">Syncing bus attendance...</span>
          </div>
        )}

        {data && !syncing && (
          <div className="space-y-3">
            {buses.length === 0 && records.length === 0 ? (
              <div className="bg-white rounded-2xl border p-8 text-center">
                <Bus className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-[#475569] font-medium">No bus attendance recorded yet</p>
                <p className="text-xs text-[#94A3B8] mt-1">Bus attendance syncs daily at 9:05 AM</p>
              </div>
            ) : (
              <>
                {/* If searching, show flat list */}
                {q ? (
                  <div className="space-y-2">
                    {filteredRecords.length === 0 ? (
                      <p className="text-center text-[#94A3B8] py-4">No results for "{searchQuery}"</p>
                    ) : (
                      filteredRecords.map((rec, i) => {
                        const markKey = `${rec.first_name}|${rec.last_name}`;
                        const isMarking = marking[markKey];
                        return (
                        <div key={i} className="bg-white rounded-xl border p-3 flex items-center justify-between">
                          <div>
                            <p className="font-semibold text-[#0F172A]">{rec.last_name}, {rec.first_name}</p>
                            <p className="text-xs text-[#475569]">{rec.bus_number}{rec.camper ? ` — ${rec.camper.bunkCode} ${rec.camper.bunkName}` : ''}</p>
                            {rec.override && <p className="text-[9px] text-blue-500 font-medium">manually marked</p>}
                          </div>
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => handleMarkStatus(rec, 'present')}
                              disabled={isMarking || rec.status === 'present'}
                              className={`px-2 py-1 rounded-md text-xs font-medium transition-all ${
                                rec.status === 'present'
                                  ? 'bg-green-600 text-white shadow-sm'
                                  : 'bg-green-100 text-green-700 hover:bg-green-200'
                              } ${isMarking ? 'opacity-50' : ''}`}
                              data-testid={`bus-search-mark-present-${rec.first_name}-${rec.last_name}`}
                            >
                              <Check className="w-3 h-3 inline mr-0.5" />On Bus
                            </button>
                            <button
                              onClick={() => handleMarkStatus(rec, 'absent')}
                              disabled={isMarking || rec.status === 'absent'}
                              className={`px-2 py-1 rounded-md text-xs font-medium transition-all ${
                                rec.status === 'absent'
                                  ? 'bg-red-600 text-white shadow-sm'
                                  : 'bg-red-100 text-red-700 hover:bg-red-200'
                              } ${isMarking ? 'opacity-50' : ''}`}
                              data-testid={`bus-search-mark-absent-${rec.first_name}-${rec.last_name}`}
                            >
                              <X className="w-3 h-3 inline mr-0.5" />Absent
                            </button>
                          </div>
                        </div>
                        );
                      })
                    )}
                  </div>
                ) : (
                  /* Bus-by-bus accordion */
                  buses.map((bus, idx) => {
                    const busRecords = recordsByBus[bus.bus_number] || [];
                    const isExpanded = expandedBus === bus.bus_number;
                    const absentCount = busRecords.filter(r => r.status === 'absent').length;
                    const lateCount = busRecords.filter(r => r.status === 'late').length;

                    return (
                      <motion.div
                        key={bus.bus_number}
                        className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] overflow-hidden"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.03 }}
                      >
                        <button
                          onClick={() => setExpandedBus(isExpanded ? null : bus.bus_number)}
                          className="w-full p-4 flex items-center justify-between text-left"
                          data-testid={`bus-card-${bus.bus_number}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                              <Bus className="w-5 h-5 text-blue-700" />
                            </div>
                            <div>
                              <p className="font-bold text-[#0F172A] font-['Fredoka']">{bus.bus_number}</p>
                              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                <span className="text-xs text-green-600">{bus.present} on bus</span>
                                {absentCount > 0 && <span className="text-xs text-red-600 font-semibold">{absentCount} absent</span>}
                                {lateCount > 0 && <span className="text-xs text-amber-600 font-semibold">{lateCount} late</span>}
                                {bus.unmarked > 0 && <span className="text-xs text-gray-500">{bus.unmarked} unmarked</span>}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className="bg-blue-100 text-blue-800 text-xs">{bus.am_camper_count} campers</Badge>
                            {isExpanded ? <ChevronUp className="w-4 h-4 text-[#94A3B8]" /> : <ChevronDown className="w-4 h-4 text-[#94A3B8]" />}
                          </div>
                        </button>

                        <AnimatePresence>
                          {isExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="border-t border-[#E2E8F0]"
                            >
                              <div className="p-3 space-y-1">
                                {busRecords.length === 0 ? (
                                  <p className="text-xs text-[#94A3B8] text-center py-2">No individual records</p>
                                ) : (
                                  busRecords
                                    .sort((a, b) => a.last_name.localeCompare(b.last_name))
                                    .map((rec, i) => {
                                      const markKey = `${rec.first_name}|${rec.last_name}`;
                                      const isMarking = marking[markKey];
                                      return (
                                      <div
                                        key={i}
                                        className={`flex items-center justify-between p-2 rounded-lg ${
                                          rec.status === 'absent' ? 'bg-red-50' :
                                          rec.status === 'late' ? 'bg-amber-50' :
                                          rec.status === 'present' ? 'bg-green-50/50' : 'bg-gray-50'
                                        }`}
                                        data-testid={`bus-record-${rec.first_name}-${rec.last_name}`}
                                      >
                                        <div>
                                          <p className="text-sm font-medium text-[#0F172A]">{rec.last_name}, {rec.first_name}</p>
                                          {rec.camper && (
                                            <p className="text-[10px] text-[#475569]">{rec.camper.bunkCode} — {rec.camper.bunkName}</p>
                                          )}
                                          {rec.override && <p className="text-[9px] text-blue-500 font-medium">manually marked</p>}
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                          <button
                                            onClick={() => handleMarkStatus(rec, 'present')}
                                            disabled={isMarking || rec.status === 'present'}
                                            className={`px-2 py-1 rounded-md text-xs font-medium transition-all ${
                                              rec.status === 'present'
                                                ? 'bg-green-600 text-white shadow-sm'
                                                : 'bg-green-100 text-green-700 hover:bg-green-200'
                                            } ${isMarking ? 'opacity-50' : ''}`}
                                            data-testid={`bus-mark-present-${rec.first_name}-${rec.last_name}`}
                                          >
                                            <Check className="w-3 h-3 inline mr-0.5" />On Bus
                                          </button>
                                          <button
                                            onClick={() => handleMarkStatus(rec, 'absent')}
                                            disabled={isMarking || rec.status === 'absent'}
                                            className={`px-2 py-1 rounded-md text-xs font-medium transition-all ${
                                              rec.status === 'absent'
                                                ? 'bg-red-600 text-white shadow-sm'
                                                : 'bg-red-100 text-red-700 hover:bg-red-200'
                                            } ${isMarking ? 'opacity-50' : ''}`}
                                            data-testid={`bus-mark-absent-${rec.first_name}-${rec.last_name}`}
                                          >
                                            <X className="w-3 h-3 inline mr-0.5" />Absent
                                          </button>
                                        </div>
                                      </div>
                                      );
                                    })
                                )}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    );
                  })
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-xl border-t border-[#E2E8F0] py-2 px-4 z-30">
        <div className="max-w-3xl mx-auto flex items-center justify-center">
        </div>
      </div>
    </div>
  );
}
